/**
 * SafeHtml — Canonical sanitised HTML renderer for TreatX frontend.
 *
 * Wave-4 remediation for FIND-H-07: every runtime raw-HTML rendering
 * surface (legal documents, CMS content, consent bodies, admin
 * previews, e-signature modals) MUST route its `dangerouslySetInnerHTML`
 * call through this single component.
 *
 * Design constraints
 * ------------------
 * 1. One and only one canonical `dangerouslySetInnerHTML` callsite is
 *    permitted in the codebase — the one inside this file. The
 *    frontend security invariant enforces that constraint.
 *
 * 2. Sanitisation runs at RENDER time and does NOT mutate any
 *    canonical stored value. Consequently, F8 legal digests and
 *    consent-evidence SHA-256 binding remain bound to the immutable
 *    canonical server-side body — this component is defence in depth
 *    at the presentation layer only.
 *
 * 3. Uses DOMPurify (industry-standard maintained sanitiser). No
 *    home-grown regex sanitisation.
 *
 * 4. External links are hardened with rel="noopener noreferrer" and
 *    target="_blank" automatically via a DOMPurify hook.
 *
 * 5. Preserves the formatting used by TreatX legal/CMS content:
 *    headings, paragraphs, lists, emphasis, tables, safe links.
 *    Rejects: <script>, event handlers (on*), javascript: URLs,
 *    <iframe>/<object>/<embed>, SVG script-hosting tags, form
 *    controls, and other executable constructs.
 */
import React, { useMemo } from 'react';
import DOMPurify from 'dompurify';

// Allowlist: legal + CMS formatting only. Explicitly does NOT include
// <script>, <iframe>, <object>, <embed>, <form>, <input>, <button>,
// <style>, <svg>, or <math>.
const ALLOWED_TAGS = [
  'a', 'b', 'blockquote', 'br', 'code', 'div', 'em', 'h1', 'h2', 'h3',
  'h4', 'h5', 'h6', 'hr', 'i', 'li', 'ol', 'p', 'pre', 'span', 'strong',
  'sub', 'sup', 'table', 'tbody', 'td', 'th', 'thead', 'tr', 'u', 'ul',
];

const ALLOWED_ATTR = [
  'class', 'colspan', 'href', 'id', 'rel', 'rowspan', 'target', 'title',
  'lang', 'dir',
];

// Only http/https/mailto for hrefs. `javascript:`, `data:` etc. are
// blocked by DOMPurify by default when this ALLOWED_URI_REGEXP is
// present.
const ALLOWED_URI_REGEXP = /^(?:(?:https?|mailto):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i;

// Harden anchor tags: force rel="noopener noreferrer" and add
// target="_blank" for external links. Idempotent — safe to run
// repeatedly.
DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  if (node.tagName === 'A') {
    const href = node.getAttribute('href') || '';
    if (/^https?:/i.test(href)) {
      node.setAttribute('rel', 'noopener noreferrer');
      if (!node.getAttribute('target')) {
        node.setAttribute('target', '_blank');
      }
    }
  }
});

/**
 * Sanitise a raw HTML string into a safe subset. Exported separately
 * so unit tests can exercise the sanitiser directly without a React
 * render.
 */
export function sanitizeTreatxHtml(raw) {
  if (raw === null || raw === undefined) return '';
  const input = typeof raw === 'string' ? raw : String(raw);
  return DOMPurify.sanitize(input, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    ALLOWED_URI_REGEXP,
    FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'style', 'svg',
                  'math', 'form', 'input', 'button', 'textarea', 'select'],
    FORBID_ATTR: ['style'],
    ALLOW_DATA_ATTR: false,
    KEEP_CONTENT: true,   // preserve inner text of stripped tags
    RETURN_TRUSTED_TYPE: false,
  });
}

/**
 * SafeHtml — renders a sanitised HTML string.
 *
 * Props
 *   html      — raw HTML string (may include admin/CMS-authored HTML)
 *   className — optional wrapper class
 *   as        — optional wrapper element (default 'div')
 *   testId    — optional data-testid pass-through
 *   emptyFallback — optional element to render when the sanitised
 *                  result is empty
 */
export function SafeHtml({
  html,
  className,
  as: Tag = 'div',
  testId,
  emptyFallback = null,
  ...rest
}) {
  const clean = useMemo(() => sanitizeTreatxHtml(html), [html]);
  if (!clean || clean.trim() === '') return emptyFallback;
  return (
    <Tag
      className={className}
      data-testid={testId}
      // eslint-disable-next-line react/no-danger -- SafeHtml is the sole canonical sanitised renderer (Wave-4 FIND-H-07).
      dangerouslySetInnerHTML={{ __html: clean }}
      {...rest}
    />
  );
}

export default SafeHtml;
